import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Key, BookOpen, Users, Check, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ConviteEscritor() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState(null);

  // Fetch invite details
  const fetchInviteDetails = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/invites/${token}`);
      setInvite(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Convite não encontrado ou inválido');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchInviteDetails();
  }, [fetchInviteDetails]);

  // Handle accept invite
  const handleAcceptInvite = useCallback(async () => {
    if (!isAuthenticated) {
      // Save token temporarily in sessionStorage (more secure than localStorage)
      sessionStorage.setItem('pendingInviteToken', token);
      navigate('/login');
      return;
    }

    setAccepting(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/invites/${token}/accept`,
        {},
        { withCredentials: true }
      );
      
      toast.success('Você agora faz parte desta obra!');
      navigate(`/livro/${response.data.book_id}`);
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Erro ao aceitar convite';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setAccepting(false);
    }
  }, [isAuthenticated, token, navigate]);

  // Check for pending invite after login
  useEffect(() => {
    const pendingToken = sessionStorage.getItem('pendingInviteToken');
    if (pendingToken && isAuthenticated && pendingToken === token) {
      sessionStorage.removeItem('pendingInviteToken');
      handleAcceptInvite();
    }
  }, [isAuthenticated, token, handleAcceptInvite]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Carregando convite...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-card border border-border rounded-lg p-8 text-center">
          <AlertCircle className="w-16 h-16 mx-auto mb-4 text-destructive" />
          <h1 className="text-2xl font-serif mb-2">Convite Inválido</h1>
          <p className="text-muted-foreground mb-6">{error}</p>
          <Button onClick={() => navigate('/')} variant="outline">
            Ir para o início
          </Button>
        </div>
      </div>
    );
  }

  const isEmailMatch = user?.email?.toLowerCase() === invite?.invited_email?.toLowerCase();

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Key className="w-10 h-10 text-amber-500" />
            <span className="text-2xl font-serif">Author's Key</span>
          </div>
          <p className="text-muted-foreground">Convite para escrever junto</p>
        </div>

        {/* Invite Card */}
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          {/* Header */}
          <div className="bg-amber-500/10 p-6 text-center border-b border-border">
            <Users className="w-12 h-12 mx-auto mb-3 text-amber-600" />
            <h1 className="text-xl font-serif mb-1">Você foi convidado!</h1>
            <p className="text-sm text-muted-foreground">
              {invite.invited_by_name} quer escrever uma história com você
            </p>
          </div>

          {/* Book Info */}
          <div className="p-6 space-y-4">
            <div className="flex items-start gap-4">
              <div className="w-12 h-16 bg-amber-500/10 rounded flex items-center justify-center shrink-0">
                <BookOpen className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <h2 className="font-serif text-lg">{invite.book_title}</h2>
                <p className="text-sm text-muted-foreground">por {invite.book_author}</p>
              </div>
            </div>

            <div className="bg-muted/50 rounded-lg p-4 text-sm">
              <p className="text-muted-foreground">
                <span className="font-medium text-foreground">Convite para:</span>{' '}
                {invite.invited_email}
              </p>
              <p className="text-muted-foreground mt-1">
                <span className="font-medium text-foreground">Permissão:</span>{' '}
                {invite.role === 'editor' ? 'Pode editar a obra' : 'Apenas leitura'}
              </p>
            </div>

            {/* Warning if email doesn't match */}
            {isAuthenticated && !isEmailMatch && (
              <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 text-sm">
                <p className="text-destructive">
                  Você está logado como <strong>{user.email}</strong>, mas este convite foi enviado para <strong>{invite.invited_email}</strong>.
                </p>
                <p className="text-muted-foreground mt-2">
                  Faça login com o email correto para aceitar.
                </p>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="p-6 pt-0 space-y-3">
            {!isAuthenticated ? (
              <>
                <Button
                  onClick={() => {
                    sessionStorage.setItem('pendingInviteToken', token);
                    navigate('/login');
                  }}
                  className="w-full bg-amber-500 hover:bg-amber-600 text-stone-900"
                >
                  Fazer login para aceitar
                </Button>
                <p className="text-xs text-center text-muted-foreground">
                  Use o email {invite.invited_email} para aceitar este convite
                </p>
              </>
            ) : isEmailMatch ? (
              <Button
                onClick={handleAcceptInvite}
                disabled={accepting}
                className="w-full bg-amber-500 hover:bg-amber-600 text-stone-900"
              >
                <Check className="mr-2" size={18} />
                {accepting ? 'Aceitando...' : 'Aceitar convite e começar a escrever'}
              </Button>
            ) : (
              <Button
                onClick={() => navigate('/login')}
                variant="outline"
                className="w-full"
              >
                Fazer login com outro email
              </Button>
            )}
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-muted-foreground mt-6">
          Ao aceitar, você terá acesso para editar esta obra junto com o autor.
        </p>
      </div>
    </div>
  );
}
